import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { FullStory } from "@fullstory/browser";
import { Flex, Text } from "@radix-ui/themes";
import { useQueryClient } from "react-query";

import { VerificationForm } from "./VerificationForm";
import { VerificationPending } from "./VerificationPending";
import { UpdateUserRequest, VerificationStatus } from "../../api/public";
import { Button } from "../../common/components/Button";
import { Callout } from "../../common/components/Callout";
import { Spinner } from "../../common/components/Spinner";
import { meQueryKey, useGetMe } from "../../hooks/useGetMe";
import { useUpdateUserProfile } from "../../hooks/useUpdateUserProfile";

type ModalStep = "info" | "form";

type Props = {
  quickVerification?: boolean;
};

export const NeedsVerifiedUser = ({
  quickVerification,
  children,
}: PropsWithChildren<Props>) => {
  const { refetch, data } = useGetMe({
    refetchOnWindowFocus: "always",
  });
  const queryClient = useQueryClient();
  const [step, setStep] = useState<ModalStep>("info");

  const { mutate, isLoading } = useUpdateUserProfile();

  useEffect(() => {
    if (data?.verification_status !== VerificationStatus.PENDING) {
      return;
    }
    const interval = setInterval(() => {
      refetch();
    }, 2000);

    return () => {
      clearInterval(interval);
    };
  }, [data?.verification_status, refetch]);

  const handleSubmit = useCallback(
    (payload: UpdateUserRequest) => {
      mutate(payload, {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        onSuccess: (_, variables, __) => {
          queryClient.invalidateQueries(meQueryKey);
          if (
            variables.contact_email &&
            variables.contact_email !== data?.contact_email
          ) {
            FullStory("trackEventAsync", {
              name: "ProfileVerificationSubmit",
              properties: {},
            });
          }
        },
      });
    },
    [data?.contact_email, mutate, queryClient]
  );

  const needsShippingDetails = !data?.shipping_details && !quickVerification;

  const [mainCopy, secondaryCopy] = useMemo(() => {
    if (data?.verification_status === VerificationStatus.NOT_VERIFIED) {
      return [
        "We'll need to verify your account before you can proceed.",
        "This process only takes about a minute, and once verified, you'll be able to view documents and make sample/support requests!",
      ];
    } else if (needsShippingDetails) {
      return [
        "You'll need to add your shipping details before you can make your first request.",
        "Since you already have a verified account, most of the work is done! Just add your shipping details and you can continue with your request.",
      ];
    }
    return "";
  }, [data?.verification_status, needsShippingDetails]);

  if (isLoading) {
    return (
      <Flex justify="center">
        <Spinner size="lg" />
      </Flex>
    );
  }

  if (
    data &&
    (data.verification_status === VerificationStatus.NOT_VERIFIED ||
      needsShippingDetails)
  ) {
    return (
      <Flex direction="column" gap="2" m="2">
        {step === "info" && (
          <Flex direction="column" gap="4">
            <Callout variant="outline" icon={"🖐️"}>
              <Flex direction="column" gap="2">
                <Text size="2" weight="bold">
                  {mainCopy}
                </Text>
                <Text size="2" color="gray">
                  {secondaryCopy}
                </Text>
              </Flex>
            </Callout>
            <Flex justify="center">
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  setStep("form");
                }}
                size="2"
                style={{ width: "100%" }}
              >
                OK, Continue
              </Button>
            </Flex>
          </Flex>
        )}
        {step === "form" && (
          <VerificationForm
            user={data}
            onSubmit={handleSubmit}
            quickVerification={quickVerification}
          />
        )}
      </Flex>
    );
  }
  if (data && data.verification_status === VerificationStatus.PENDING) {
    return <VerificationPending contactEmail={data.contact_email ?? ""} />;
  }

  return <>{children}</>;
};
